<!--
  The event ticket: one box. Date, price and seats for everyone, the settings
  rows while editing — same surface, same place. While editing, two tabs keep
  every event setting on the ticket without stacking them all: the date
  (with status) and the registration (with price, seats and who is
  notified). A cancelled or postponed event says so in a section of its own;
  the registration section is rendered by the parent via `children`.
-->
<script>
  import t, { LANG } from '#lib/i18n.svelte.js';
  import DateTimeRange from '#lib/DateTimeRange.svelte';
  import SeriesControls from '#lib/SeriesControls.svelte';
  import SegmentedControl from '#lib/SegmentedControl.svelte';
  import TextInput from '#lib/TextInput.svelte';
  import IntegerInput from '#lib/IntegerInput.svelte';
  import FormLabel from '#lib/FormLabel.svelte';
  import EventChip from '#lib/EventChip.svelte';
  import { get_event_capacity } from '#lib/event_capacity.js';
  import { get_min_event_price_amount, is_zero_decimal_currency } from '#lib/app_config.js';
  import { format_amount_input, parse_amount_input, format_currency_symbol, format_utc_date_as_local } from '#lib/util.js';
  import { untrack } from 'svelte';
  import '#lib/system-styles/event-ui.css';

  let {
    entry,
    additional_entry_data,
    is_editing = false,
    is_admin = false,
    /** Cancelled or postponed: the ticket says so instead of price and seats */
    is_restricted = false,
    /** Hide price and seats (cancelled/postponed or concluded events) */
    show_availability = true,
    /** The ticket's root, for the entry to scroll into view on arrival */
    element = $bindable(),
    children,
  } = $props();

  let capacity = $derived(get_event_capacity(entry, additional_entry_data));
  let is_internal = $derived(entry.registration_mode === 'internal');

  /** 'schedule' | 'registration' */
  let active_tab = $state('schedule');
  const tab_ids = $props.id();
  const tabs = $derived([
    { value: 'schedule', title: t('event_tab_schedule') },
    { value: 'registration', title: t('event_tab_registration') },
  ]);

  // Arrow keys move between the tabs, as in any tab list
  function handle_tab_keydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = tabs.findIndex(tab => tab.value === active_tab);
    const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    active_tab = next.value;
    document.getElementById(`${tab_ids}-tab-${next.value}`)?.focus();
  }

  const status_options = $derived([
    { value: 'scheduled', title: t('event_status_option_scheduled') },
    { value: 'cancelled', title: t('event_status_cancelled') },
    { value: 'postponed', title: t('event_status_postponed') },
  ]);

  // Price is stored in the currency's smallest unit; admins type a decimal amount.
  // Reseed the field when the model itself is swapped (cancel, reload, navigation
  // between events) so the input never shows a stale amount over a fresh model.
  let price_input = $state(untrack(() => format_amount_input(entry?.price_amount, LANG)));
  // An empty field means free; a value that does not parse is an error, not a
  // silent switch to free — keep the last valid price and surface the problem.
  let price_invalid = $state(false);
  $effect(() => {
    entry;
    untrack(() => {
      price_input = format_amount_input(entry?.price_amount, LANG);
      price_invalid = false;
    });
  });

  function handle_price_input(event) {
    price_input = event.target.value;
    if (price_input.trim() === '') {
      price_invalid = false;
      entry.price_amount = undefined;
      return;
    }
    const parsed = parse_amount_input(price_input, LANG);
    if (parsed === null) {
      price_invalid = true;
      return;
    }
    price_invalid = false;
    entry.price_amount = parsed;
  }

  // Clearing the seats field means unlimited (null); the input reports an
  // empty field as undefined, which the capacity rule reads as the legacy 30
  function set_max_attendees(value) {
    entry.max_attendees = value === undefined ? null : value;
  }

  let currency_symbol = $derived(format_currency_symbol(entry.currency_code, LANG));

  // A price below Stripe's minimum charge looks paid to members but fails at
  // checkout, so the admin must see it while typing. The minimum depends on
  // the currency (¥50 is stored as 5000), and a currency without minor units
  // takes whole units only.
  let min_price_amount = $derived(get_min_event_price_amount(entry.currency_code));
  let price_too_low = $derived(
    Number.isInteger(entry.price_amount) && entry.price_amount > 0 && entry.price_amount < min_price_amount
  );
  let price_not_whole = $derived(
    Number.isInteger(entry.price_amount) && is_zero_decimal_currency(entry.currency_code) && entry.price_amount % 100 !== 0
  );
  let min_price_display = $derived(format_amount_input(min_price_amount, LANG));

  let price_line = $derived(
    entry.payment_required ? t('price_per_person', { price: entry.formatted_price }) : t('free_of_charge')
  );

  // Seats left or waitlist spots, only when it matters. When the admin turns
  // off "show remaining seats" the counts stay private — the numbers reveal
  // attendance either way — so only the admin still sees them.
  let seats_note = $derived.by(() => {
    if (!entry.display_capacity && !is_admin) return '';
    if (capacity.is_unlimited) return '';
    if (capacity.has_waitlist_space) {
      return capacity.remaining_waitlist === 1
        ? t('waitlist_spot_singular')
        : t('waitlist_spots', { count: capacity.remaining_waitlist });
    }
    if (capacity.is_full) return '';
    if (capacity.is_low) {
      return capacity.remaining_seats === 1
        ? t('remaining_spot_singular')
        : t('remaining_spots', { count: capacity.remaining_seats });
    }
    if (entry.display_capacity) {
      return t('seats_remaining_of', { remaining: capacity.remaining_seats, total: capacity.max_attendees });
    }
    return '';
  });
</script>

<aside class='event-ticket' bind:this={element}>

  {#if is_editing}
    <!---------------------------------------------------------------------------------->
    <!-------------------------- Settings (admin, editing) ----------------------------->
    <!---------------------------------------------------------------------------------->
    <div class='event-ticket-tabs' role='tablist'>
      {#each tabs as tab (tab.value)}
        <button
          type='button'
          role='tab'
          id='{tab_ids}-tab-{tab.value}'
          class='event-ticket-tab text-control'
          aria-selected={active_tab === tab.value}
          aria-controls='{tab_ids}-panel-{tab.value}'
          tabindex={active_tab === tab.value ? 0 : -1}
          onclick={() => { active_tab = tab.value; }}
          onkeydown={handle_tab_keydown}
        >
          {tab.title}
        </button>
      {/each}
    </div>

    <!-- Both panels stay mounted, so a field keeps what was typed while the other tab is open -->
    <div
      class='event-ticket-section event-compact event-rows -stacked'
      role='tabpanel'
      id='{tab_ids}-panel-schedule'
      aria-labelledby='{tab_ids}-tab-schedule'
      hidden={active_tab !== 'schedule'}
    >
      <DateTimeRange
        bind:all_day={entry.all_day}
        bind:starts_at={entry.starts_at}
        bind:ends_at={entry.ends_at}
        date_min={format_utc_date_as_local(new Date())}
        {is_editing}
        class='event-rows'
        variant='brand ghostly'
        input_class='event-field'
      />

      <SeriesControls {entry} {is_editing} />

      <div class='event-row'>
        <span class='text-control event-label'>{t('event_status_question')}</span>
        <div class='event-value'>
          <SegmentedControl bind:value={entry.event_status} options={status_options} />
        </div>
      </div>
    </div>

    <div
      class='event-ticket-section event-compact event-rows -stacked'
      role='tabpanel'
      id='{tab_ids}-panel-registration'
      aria-labelledby='{tab_ids}-tab-registration'
      hidden={active_tab !== 'registration'}
    >
      <div class='event-row'>
        <span class='text-control event-label'>{t('registration_mode_question')}</span>
        <div class='event-value'>
          <SegmentedControl
            bind:value={entry.registration_mode}
            options={[
              { value: 'internal', title: t('registration_mode_option_here') },
              { value: 'external', title: t('registration_mode_option_link') },
              { value: 'disabled', title: t('registration_mode_option_none') },
            ]}
          />
        </div>
      </div>

      {#if is_internal}
        <div class='event-row'>
          <FormLabel for='event_price_amount'>{t('price_per_person_label')}</FormLabel>
          <div class='event-value'>
            <TextInput
              id='event_price_amount'
              class='event-field w-10'
              variant='brand ghostly'
              value={price_input}
              oninput={handle_price_input}
              placeholder={t('price_empty_hint')}
              inputmode='decimal'
            />
            <TextInput
              id='event_price_currency'
              class='event-field w-4'
              variant='brand ghostly'
              value={entry.price_currency}
              oninput={(event) => { entry.price_currency = event.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3); }}
              placeholder='EUR'
              aria-label={t('event_price_currency')}
              autocapitalize='characters'
            />
          </div>
        </div>
        {#if price_invalid || price_not_whole}
          <div class='event-row -unlabelled'>
            <p class='text-control text-error leading-trim'>{t('amount_invalid')}</p>
          </div>
        {:else if entry.payment_required && (entry.price_currency || '').length !== 3}
          <div class='event-row -unlabelled'>
            <p class='text-control text-error leading-trim'>{t('currency_code_invalid')}</p>
          </div>
        {:else if price_too_low}
          <div class='event-row -unlabelled'>
            <p class='text-control text-error leading-trim'>{t('price_below_minimum', { min: `${min_price_display} ${currency_symbol}` })}</p>
          </div>
        {:else if entry.payment_required && additional_entry_data?.stripe_payments_configured === false}
          <div class='event-row -unlabelled'>
            <p class='text-control text-2'>{t('stripe_not_configured_warning')}</p>
          </div>
        {/if}

        <div class='event-row'>
          <FormLabel for='event_max_attendees'>{t('seat_count_label')}</FormLabel>
          <div class='event-value'>
            <IntegerInput
              id='event_max_attendees'
              class='event-field w-8 fit-content'
              variant='brand flush'
              show_spinners={false}
              min={1}
              bind:value={() => entry.max_attendees, set_max_attendees}
              placeholder={t('unlimited')}
            />
            <label class='event-check text-control'>
              <input type='checkbox' bind:checked={entry.display_capacity} />
              {t('show_remaining_seats_public')}
            </label>
          </div>
        </div>
        <div class='event-row'>
          <FormLabel for='event_max_guests_per_registration'>{t('max_guests_per_registration_label')}</FormLabel>
          <div class='event-value'>
            <IntegerInput
              id='event_max_guests_per_registration'
              class='event-field w-4 fit-content'
              variant='brand flush'
              show_spinners={false}
              min={1}
              bind:value={entry.max_guests_per_registration}
            />
          </div>
        </div>

        <!-- Paid seats are held by Stripe until checkout; no waitlist for them -->
        {#if !entry.payment_required}
          <div class='event-row'>
            <FormLabel for='event_waitlist_capacity'>{t('waitlist_seats_label')}</FormLabel>
            <div class='event-value'>
              <IntegerInput
                id='event_waitlist_capacity'
                class='event-field w-6 fit-content'
                variant='brand flush'
                show_spinners={false}
                min={0}
                bind:value={entry.waitlist_capacity}
                placeholder='0'
              />
            </div>
          </div>
          {#if (entry.waitlist_capacity || 0) < capacity.waitlist_count}
            <div class='event-row -unlabelled'>
              <p class='text-control text-2'>
                {t('waitlist_capacity_warning', { current: capacity.waitlist_count, new: entry.waitlist_capacity || 0 })}
              </p>
            </div>
          {/if}
        {/if}
      {/if}

      {#if entry.registration_mode === 'external'}
        <div class='event-row'>
          <FormLabel for='event_link_href'>{t('registration_link_label')}</FormLabel>
          <div class='event-value'>
            <TextInput
              id='event_link_href'
              class='event-field w-full'
              variant='brand ghostly'
              bind:value={entry.link_href}
              placeholder={t('registration_link_placeholder')}
              inputmode='url'
            />
          </div>
        </div>
      {/if}

      {#if entry.registration_mode !== 'disabled'}
        <div class='event-row'>
          <FormLabel for='event_link_button_label'>{t('registration_button_text_label')}</FormLabel>
          <div class='event-value'>
            <TextInput
              id='event_link_button_label'
              class='event-field w-full'
              variant='brand ghostly'
              bind:value={entry.link_button_label}
              placeholder={t('register_now')}
            />
          </div>
        </div>
      {/if}

      <!-- Only registrations made here are known to the site, so only they notify -->
      {#if is_internal}
        <div class='event-row'>
          <FormLabel for='event_registration_notification_email'>{t('registration_notification_label')}</FormLabel>
          <div class='event-value'>
            <TextInput
              id='event_registration_notification_email'
              class='event-field w-full'
              variant='brand ghostly'
              bind:value={entry.registration_notification_email}
              placeholder={t('email')}
              type='email'
              inputmode='email'
            />
          </div>
        </div>
      {/if}
    </div>

  {:else}
    <!---------------------------------------------------------------------------------->
    <!-------------------------- Date, price and seats --------------------------------->
    <!---------------------------------------------------------------------------------->
    <div class='event-ticket-section event-lines'>
      <DateTimeRange
        starts_at={entry.starts_at}
        ends_at={entry.ends_at}
        all_day={entry.all_day}
        is_editing={false}
        view_class='text-control -semibold'
      />
      {#if is_internal && show_availability}
        <p class='text-control event-facts'>
          <span class='leading-trim'>{price_line}</span>
          {#if seats_note}
            <span class='text-2 leading-trim'>{seats_note}</span>
          {/if}
        </p>
      {/if}
    </div>

    <!---------------------------------------------------------------------------------->
    <!-------------------------- Cancelled or postponed -------------------------------->
    <!---------------------------------------------------------------------------------->
    {#if is_restricted}
      <div class='event-ticket-section event-lines'>
        <div class='event-compact event-facts'>
          <EventChip tone='warn'>
            {t(entry.event_status === 'postponed' ? 'event_status_postponed' : 'event_status_cancelled')}
          </EventChip>
        </div>
        <p class='text-control text-2 leading-trim'>
          {t(entry.event_status === 'postponed' ? 'event_postponed_notice' : 'event_cancelled_notice')}
        </p>
      </div>
    {/if}

    {@render children?.()}
  {/if}
</aside>
